import React, { useEffect, useMemo, useState } from "react";
import api, { formatTL, formatDateTime, formatDuration } from "@/lib/api";
import { invalidate, TAGS } from "@/lib/liveSync";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plate } from "@/components/Plate";
import { toast } from "sonner";
import { HandCoins, Trash } from "@phosphor-icons/react";

const METHOD_LABELS = { cash: "Nakit", card: "Kart", bank_transfer: "EFT / Havale" };

export default function BusinessReceivablesDialog({ account, onClose }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!account) return;
    try {
      const { data: d } = await api.get(`/business-agreements/${account.id}/receivables`);
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Yüklenemedi"); }
  };
  useEffect(() => { setData(null); setSelected({}); load(); }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedTotal = useMemo(
    () => (data?.pending || []).filter((s) => selected[s.id]).reduce((a, s) => a + (s.business_fee || 0), 0),
    [data, selected],
  );

  const toggleAll = (v) => {
    const next = {};
    (data?.pending || []).forEach((s) => { next[s.id] = !!v; });
    setSelected(next);
  };

  const collect = async () => {
    if (!selectedIds.length) return toast.error("Oturum seçin");
    if (!window.confirm(`${selectedIds.length} araç · ${formatTL(selectedTotal)} ${METHOD_LABELS[method]} olarak tahsil edilsin mi? Kasa'ya manuel gelir yazılacak.`)) return;
    setBusy(true);
    try {
      await api.post(`/business-agreements/${account.id}/collect`, { session_ids: selectedIds, method, note: note || null });
      toast.success(`${formatTL(selectedTotal)} tahsil edildi — Kasa'ya işlendi`);
      setSelected({}); setNote("");
      invalidate([TAGS.KASA, TAGS.SESSIONS, TAGS.CORPORATE]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Tahsilat başarısız"); }
    finally { setBusy(false); }
  };

  const cancel = async (col) => {
    if (!window.confirm(`${formatTL(col.amount)} (${col.vehicle_count} araç) tahsilatı iptal edilsin mi? Kasa'daki manuel gelir silinecek.`)) return;
    try {
      await api.delete(`/business-collections/${col.id}`);
      toast.success("Tahsilat iptal edildi — oturumlar tekrar bekliyor");
      invalidate([TAGS.KASA, TAGS.SESSIONS, TAGS.CORPORATE]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İptal edilemedi"); }
  };

  if (!account) return null;
  const pending = data?.pending || [];
  return (
    <Dialog open={!!account} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandCoins size={18} /> {account.name} — İşletmeden Bekleyen</DialogTitle>
          <DialogDescription>İşletme payı tahsilatı müşteri ödemesinden bağımsızdır; tahsilat Kasa'ya manuel gelir olarak işlenir.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-muted-foreground py-6 text-center">Yükleniyor…</div> : (
          <Tabs defaultValue="pending">
            <TabsList className="rounded-sm">
              <TabsTrigger value="pending" data-testid="recv-tab-pending">Bekleyen ({data.pending_count})</TabsTrigger>
              <TabsTrigger value="collected" data-testid="recv-tab-collected">Tahsil Edilen ({data.collected.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-3">
              <div className="p-3 rounded-sm bg-orange-50 border border-orange-200 flex items-center justify-between">
                <div className="text-sm font-semibold" data-testid="recv-pending-summary">
                  {data.pending_count} araç · <span className="font-mono text-orange-700">{formatTL(data.pending_total)}</span> tahsilat bekliyor
                </div>
                {isSuper && pending.length > 0 && (
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={selectedIds.length === pending.length} onCheckedChange={toggleAll} data-testid="recv-select-all" /> Tümünü seç
                  </label>
                )}
              </div>
              <div className="max-h-[40vh] overflow-y-auto border border-border rounded-sm">
                <Table>
                  <TableHeader><TableRow>
                    {isSuper && <TableHead className="w-8"></TableHead>}
                    <TableHead>Plaka</TableHead><TableHead>Giriş → Çıkış</TableHead><TableHead>Süre</TableHead>
                    <TableHead className="text-right">İşletme Payı</TableHead><TableHead>Müşteri</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pending.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Bekleyen yok</TableCell></TableRow>}
                    {pending.map((s) => (
                      <TableRow key={s.id} data-testid={`recv-pending-${s.plate}`}>
                        {isSuper && <TableCell><Checkbox checked={!!selected[s.id]} onCheckedChange={(v) => setSelected({ ...selected, [s.id]: !!v })} data-testid={`recv-check-${s.plate}`} /></TableCell>}
                        <TableCell><Plate>{s.plate}</Plate></TableCell>
                        <TableCell className="text-xs font-mono">{formatDateTime(s.entry_time)} → {formatDateTime(s.exit_time)}</TableCell>
                        <TableCell className="text-xs font-mono">{formatDuration(s.duration_minutes)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-orange-700">{formatTL(s.business_fee)}</TableCell>
                        <TableCell className="text-xs">{formatTL(s.fee)} · <span className="uppercase">{s.payment_status}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {isSuper && pending.length > 0 && (
                <div className="p-3 rounded-sm border border-border grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Not (opsiyonel)</div>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm h-10" placeholder="Fatura no / açıklama" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Yöntem</div>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger className="rounded-sm h-10 w-40" data-testid="recv-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Nakit</SelectItem>
                        <SelectItem value="card">Kart</SelectItem>
                        <SelectItem value="bank_transfer">EFT / Havale</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={collect} disabled={busy || !selectedIds.length} data-testid="recv-collect-btn"
                          className="rounded-sm h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
                    <HandCoins size={16} /> {selectedIds.length} araç · {formatTL(selectedTotal)} Tahsil Et
                  </Button>
                </div>
              )}
              {!isSuper && <div className="text-xs text-muted-foreground">Tahsilat işlemini yalnızca süperadmin yapabilir.</div>}
            </TabsContent>

            <TabsContent value="collected" className="space-y-3">
              <div className="text-sm font-semibold">Toplam tahsil edilen: <span className="font-mono text-emerald-700" data-testid="recv-collected-total">{formatTL(data.collected_total)}</span></div>
              <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                {data.collections.length === 0 && <div className="text-center text-muted-foreground text-sm py-6">Tahsilat kaydı yok</div>}
                {data.collections.map((c) => (
                  <div key={c.id} className="p-3 border border-border rounded-sm" data-testid={`recv-collection-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm">
                        <span className="font-mono font-bold text-emerald-700">{formatTL(c.amount)}</span> · {c.vehicle_count} araç ·{" "}
                        <Badge variant="outline" className="rounded-sm text-[9px] uppercase">{METHOD_LABELS[c.method] || c.method}</Badge>
                        <div className="text-[11px] text-muted-foreground">{formatDateTime(c.collected_at)} · {c.collected_by}{c.note ? ` · ${c.note}` : ""}</div>
                      </div>
                      {isSuper && (
                        <Button variant="ghost" size="sm" onClick={() => cancel(c)} className="text-destructive" data-testid={`recv-cancel-${c.id}`}>
                          <Trash size={14} /> İptal
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">{(c.plates || []).map((p) => <Plate key={p}>{p}</Plate>)}</div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
